import React, { useEffect, useRef, useCallback } from 'react';
import { POST_MESSAGE_NAME } from '../constants';
import { IPaymentConnectorPostMessage } from '~/interfaces/commerce-types';

export interface IProgressiveIFrameElementProps {
  className?: string;
  moduleName?: string;
  iframeAriaLabel: string;
  displayContent?: string;
  sourceUrl?: string;
  requestUrlOrigin?: string;
  messageOrigin?: string;
  css?: string;
  height?: number | string;
  width?: number | string;
  onIFrameMessage?(event: MessageEvent): void;
  progLeaseID?: string;
}

const getHostName = (url: string = ''): string => {
  return (
    url.includes('//') ? url.split('/')[2] : url.split('/')[0]
  ).toLowerCase();
};

const ProgressiveLeaseSignIframe: React.FC<IProgressiveIFrameElementProps> = ({
  className,
  iframeAriaLabel,
  displayContent,
  sourceUrl,
  requestUrlOrigin,
  messageOrigin,
  css,
  height,
  width,
  onIFrameMessage,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const postMessage = useCallback(
    (parameters: IPaymentConnectorPostMessage) => {
      if (iframeRef.current?.contentWindow?.postMessage) {
        iframeRef.current.contentWindow.postMessage(
          parameters.message,
          parameters.targetOrigin,
        );
      }
    },
    [],
  );

  useEffect(() => {
    // Example of calling postMessage after component mounts
    if (iframeRef.current) {
      postMessage({ message: 'Hello iframe!', targetOrigin: '*' });
    }
  }, [postMessage]);

  // Handle incoming messages
  useEffect(() => {
    const handleEvent = (event: MessageEvent) => {
      const sourceHost = getHostName(sourceUrl);
      const eventHost = getHostName(event.origin);
      const requestHost = getHostName(requestUrlOrigin);
      const messageHost = getHostName(messageOrigin);

      // Security check: ensure the origin matches
      if (
        !onIFrameMessage ||
        (sourceUrl &&
          !(
            sourceHost === eventHost ||
            requestHost === eventHost ||
            messageHost === eventHost
          )) ||
        (!sourceUrl &&
          !(requestHost === eventHost || messageHost === eventHost))
      ) {
        return;
      }

      onIFrameMessage(event);
    };

    window.addEventListener(POST_MESSAGE_NAME, handleEvent);

    return () => {
      window.removeEventListener(POST_MESSAGE_NAME, handleEvent);
    };
  }, [sourceUrl, requestUrlOrigin, messageOrigin, onIFrameMessage]);

  // Update content document if needed
  useEffect(() => {
    const iframeCurrent = iframeRef.current;
    if (!sourceUrl && displayContent && iframeCurrent) {
      const innerDocument = iframeCurrent.contentDocument;

      if (innerDocument) {
        innerDocument.open();
        innerDocument.write(displayContent);
        if (css) {
          const style = document.createElement('style');
          const cssNote = document.createTextNode(css);
          style.appendChild(cssNote);
          innerDocument.head.appendChild(style);
        }

        innerDocument.close();
      }
    }
  }, [displayContent, sourceUrl, css]);

  return (
    <iframe
      title={iframeAriaLabel}
      className={className}
      aria-label={iframeAriaLabel}
      src={sourceUrl}
      ref={iframeRef}
      height={height}
      width={width}
      sandbox="allow-scripts allow-forms allow-same-origin allow-popups"
    />
  );
};

export default ProgressiveLeaseSignIframe;
